import { useCallback } from 'react';

import { useBraze } from '../../BrazeProvider';
import { type BrazePushNotificationPayload } from '../../client';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook that sets up a listener for when a Braze push notification is received.
 *
 * Can be used as an effect (`useEffect`, `useFocusEffect`) or in response to an action.
 *
 * NOTE: At the moment of writing, Braze SDK does not support push notification
 *       listeners on the Web platform. We still keep the internal event
 *       listener logic in place to have the same interface, but it won't be
 *       used on the Web platform.
 *
 * @see {@link https://github.com/braze-inc/braze-web-sdk/issues/164}
 */
export const useOnBrazePushNotificationReceived = (
  params: UseOnBrazePushNotificationReceivedParams,
) => {
  const { onPushNotificationReceived } = params;

  const { addPushNotificationReceivedListener } = useBraze();

  return useCallback(() => {
    const removeListener = addPushNotificationReceivedListener(
      onPushNotificationReceived,
    );

    return () => {
      removeListener?.();
    };
  }, [addPushNotificationReceivedListener, onPushNotificationReceived]);
};

// ─── Types ───────────────────────────────────────────────────────────────────

type UseOnBrazePushNotificationReceivedParams = {
  onPushNotificationReceived: (
    notificationPayload?: BrazePushNotificationPayload,
  ) => void;
};
